<template>
  <div class="consulta-publica container-fluid p-0">
    <!-- Header full width -->
    <div class="doc-header w-100 mb-2 p-2">
      <div class="d-flex flex-column">
        <h4 class="mb-1">
          Assinatura eletrônica de documentos
        </h4>
        <div class="meta">
          <div>Identificador: <strong>#{{ identificador }}</strong></div>
          <div>Documento: <strong>{{ documentoNomeExibicao }}</strong></div>
          <div class="mt-1">
            Chave pública do documento: <code class="chave">{{ chavePublica || '-' }}</code>
          </div>
        </div>
      </div>
    </div>

    <div class="row no-gutters">
      <div class="col-md-8 pr-md-2">
        <h5 class="sr-only">
          Visualização
        </h5>

        <div
          v-if="isLoading"
          class="alert alert-info mb-2"
        >
          Carregando...
        </div>
        <div
          v-if="error"
          class="alert alert-danger mb-2"
        >
          {{ error }}
        </div>

        <div
          v-if="arquivoUrl"
          class="pdf-viewer mb-2"
        >
          <iframe
            :src="pdfViewerUrl"
            frameborder="0"
            width="100%"
            height="75vh"
            title="Visualização do documento"
          />
        </div>

        <div
          v-else-if="!isLoading"
          class="no-file alert alert-secondary mb-2"
        >
          Arquivo não disponível
        </div>
      </div>

      <div class="col-md-4 pl-md-2">
        <div
          v-if="documento && documento.documentoAssinatura"
          class="info-panel"
        >
          <div class="card">
            <div class="card-body p-2">
              <h6 class="card-title mb-1 text-truncate">
                {{ documento.documentoAssinatura.nomeArquivo }}
              </h6>
              <div class="small text-muted mb-1">
                Tipo: {{ documento.documentoAssinatura.mimeType || '-' }}
              </div>
              <div
                v-if="documento.documentoAssinatura.dataModificacao"
                class="small text-muted mb-2"
              >
                Atualizado em: {{ formattedDate }}
              </div>
              <a
                v-if="arquivoUrl"
                :href="arquivoUrl"
                target="_blank"
                rel="noopener"
                class="btn btn-primary btn-sm"
                download
              >
                Baixar Cópia
              </a>
            </div>
          </div>

          <div class="card">
            <div class="card-body p-2 scrollable">
              <h6 class="mb-2">
                Envolvidos
              </h6>
              <ul class="list-unstyled mb-0">
                <li
                  v-for="assinante in documento.documentoAssinatura.documentoAssinantes || []"
                  :key="assinante.id"
                  class="pb-1 mb-1 border border-success bg-light rounded p-1"
                >
                  <p class="font-weight-bold p-0 m-0">
                    {{ assinante.email || '-' }}
                  </p>
                  <p class="small p-0 m-0">
                    {{ assinante.tipoAssinante || '-' }}
                  </p>
                </li>
                <li
                  v-if="!(documento.documentoAssinatura.documentoAssinantes || []).length"
                  class="small text-muted"
                >
                  Nenhum envolvido encontrado.
                </li>
              </ul>
            </div>
          </div>

          <div class="card">
            <div class="card-body p-2 scrollable">
              <h6 class="mb-2">
                Histórico
              </h6>
              <div class="mb-0">
                <div
                  v-for="(h, idx) in documento.documentoAssinatura.historicos || []"
                  :key="idx"
                  class="pb-1 mb-1 card p-2"
                >
                  <div><strong>{{ h.acao }}</strong> — <span class="text-muted">{{ h.usuario || '-' }}</span></div>
                  <div class="small text-muted">
                    {{ h.titulo }}
                  </div>
                  <div class="small">
                    {{ formatDateTime(h.dataAcao) }}
                  </div>
                  <div class="mt-1 small">
                    {{ h.descricao }}
                  </div>
                </div>
                <div
                  v-if="!(documento.documentoAssinatura.historicos || []).length"
                  class="small text-muted"
                >
                  Nenhum histórico disponível.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="alert alert-secondary"
        >
          Informações do documento não encontradas.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import docuSign from '@/shared/mixins/docu-sign/docu-sign'

export default {
  name: 'ConsultaPublicaDocumentoAssinatura',
  data() {
    return {
      documento: null,
      arquivoUrl: null,
      isLoading: false,
      error: null,
    }
  },
  computed: {
    pdfViewerUrl() {
      if (!this.arquivoUrl) return ''
      return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(this.arquivoUrl)}`
    },
    formattedDate() {
      const d = this.documento && this.documento.documentoAssinatura && this.documento.documentoAssinatura.dataModificacao
      return d ? moment(d).format('DD/MM/YYYY HH:mm') : '-'
    },
    identificador() {
      return (this.documento && this.documento.documentoAssinatura && this.documento.documentoAssinatura.id) || '-'
    },
    documentoNomeExibicao() {
      return (this.documento && this.documento.documentoAssinatura && this.documento.documentoAssinatura.nomeArquivo) || '-'
    },
    chavePublica() {
      // tenta obter da raiz do histórico ou do próprio objeto
      const hist = this.documento && this.documento.documentoAssinatura && this.documento.documentoAssinatura.historicos
      if (hist && hist.length && hist[0].chavePublicaDocumento) return hist[0].chavePublicaDocumento
      return (this.documento && this.documento.documentoAssinatura && this.documento.documentoAssinatura.chavePublicaDocumento) || ''
    },
  },
  created() {
    const chavePublica = this.$route && this.$route.params ? this.$route.params.publicKey : null
    if (chavePublica) {
      this.getDocumentoInfo(chavePublica)
    } else {
      this.error = 'Chave pública não informada'
    }
  },
  methods: {
    async getDocumentoInfo(chavePublica) {
      this.isLoading = true
      this.error = null
      try {
        const resp = await docuSign.getDadosDocumentoByChavePublica(chavePublica)
        this.documento = resp.data
        if (this.documento && this.documento.documentoAssinatura && this.documento.documentoAssinatura.id) {
          const arquivoResp = this.documento.visualizacaoDocumento
          this.arquivoUrl = arquivoResp && arquivoResp.url ? arquivoResp.url : null
        }
      } catch (err) {
        this.error = 'Erro ao obter dados do documento.'
      } finally {
        this.isLoading = false
      }
    },
    formatDateTime(v) {
      if (!v) return '-'
      const m = moment(v)
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : v
    },
  },
}
</script>

<style scoped>
/* Header */
.doc-header {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}
.doc-header h4 {
  margin: 0 0 0.25rem 0;
  font-weight: 600;
}
.doc-header .meta { font-size: 0.95rem; color: #333; }
.doc-header .chave {
  display: block;
  margin-top: 0.25rem;
  font-family: monospace;
  font-size: 0.85rem;
  color: #555;
  background: transparent;
  padding: 0;
  border: none;
  word-break: break-all;
}

/* Make cards compact and remove shadow */
.card {
  box-shadow: none !important;
  border: 1px solid #e9ecef;
  margin-bottom: 0.5rem;
  border-radius: 6px;
}
.card-body p-2 {
  padding: 0.5rem 0.75rem;
}
.card-title {
  margin-bottom: 0;
  font-size: 0.95rem;
}

/* Compact lists */
.info-panel .list-unstyled li {
  padding: 0.25rem 0;
  margin: 0;
  border-bottom: none;
}

/* PDF viewer adjustments */
.pdf-viewer iframe {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #fff;
  height: 98vh;
}

/* Reduce overall spacing */
.no-file, .alert {
  margin-bottom: 0.5rem;
}

/* Make Envolvidos and Histórico scrollable when exceed 90vh */
.info-panel .card .card-body.scrollable {
  max-height: 80vh;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

/* Responsive tweaks */
@media (max-width: 767px) {
  .doc-header { padding: 0.75rem; }
  .doc-header h4 { font-size: 1rem; }
  .pdf-viewer iframe { height: 55vh; }
}
</style>
